import { useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { DatePicker } from './date-picker';
import { TimePicker } from './time-picker';

export interface DateTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  step?: number;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-label'?: string;
}

function split(value: string): [string, string] {
  const [date = '', time = ''] = value.split('T');
  return [date, time.slice(0, 5)];
}

export function DateTimePicker({
  value,
  onChange,
  min,
  max,
  step,
  required,
  disabled,
  id,
  className,
  ...aria
}: DateTimePickerProps) {
  const [date, setDate] = useState(() => split(value)[0]);
  const [time, setTime] = useState(() => split(value)[1]);
  const lastEmitted = useRef(value);
  const timeWordId = useId();

  useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    const [nextDate, nextTime] = split(value);
    setDate(nextDate);
    setTime(nextTime);
  }, [value]);

  const emit = (nextDate: string, nextTime: string) => {
    setDate(nextDate);
    setTime(nextTime);
    const next = nextDate && nextTime ? `${nextDate}T${nextTime}` : '';
    lastEmitted.current = next;
    if (next !== value) onChange(next);
  };

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      <DatePicker
        id={id}
        value={date}
        min={min?.slice(0, 10)}
        max={max?.slice(0, 10)}
        required={required}
        disabled={disabled}
        onChange={(next) => emit(next, time)}
        aria-label={aria['aria-label'] ? `${aria['aria-label']} date` : undefined}
        aria-describedby={aria['aria-describedby']}
        aria-invalid={aria['aria-invalid']}
        className="min-w-[9.5rem] flex-[3]"
      />
      <TimePicker
        value={time}
        step={step}
        required={required}
        disabled={disabled}
        onChange={(next) => emit(date, next)}
        aria-label={aria['aria-label'] ? `${aria['aria-label']} time` : undefined}
        aria-labelledby={aria['aria-label'] ? undefined : id ? `${id}-label ${timeWordId}` : timeWordId}
        aria-describedby={aria['aria-describedby']}
        aria-invalid={aria['aria-invalid']}
        className="min-w-[7.5rem] flex-[2]"
      />
      <span id={timeWordId} hidden>
        time
      </span>
    </div>
  );
}
